import {
  disabledLandingSections,
  landingHomeSectionKeys,
  type LandingHomeSectionKey,
} from './landingEditorTypes.js';

export type LandingEditorSectionCategory =
  | 'header'
  | 'content'
  | 'cards'
  | 'trust'
  | 'conversion';

export type LandingEditorSectionPreviewLayout =
  | 'hero'
  | 'split'
  | 'cards'
  | 'quote'
  | 'process'
  | 'logos'
  | 'form';

export interface LandingEditorSectionLibraryEntry {
  key: LandingHomeSectionKey;
  label: string;
  category: LandingEditorSectionCategory;
  previewLayout: LandingEditorSectionPreviewLayout;
  icon: string;
  heroSlot?: 'company';
  archived?: boolean;
}

const entry = (
  key: LandingHomeSectionKey,
  label: string,
  category: LandingEditorSectionCategory,
  previewLayout: LandingEditorSectionPreviewLayout,
  icon: string,
  metadata: Pick<LandingEditorSectionLibraryEntry, 'heroSlot' | 'archived'> = {},
): LandingEditorSectionLibraryEntry => ({ key, label, category, previewLayout, icon, ...metadata });

export const landingEditorSectionLibrary: Record<LandingHomeSectionKey, LandingEditorSectionLibraryEntry> = {
  hero: entry('hero', 'Hero', 'header', 'hero', 'icon-[material-symbols--web-asset]'),
  heroApplicant: entry('heroApplicant', 'Hero Bewerber', 'header', 'hero', 'icon-[material-symbols--person-search]'),
  heroCompany: entry('heroCompany', 'Hero Unternehmen', 'header', 'hero', 'icon-[material-symbols--corporate-fare]', { heroSlot: 'company' }),
  heroFanApplicant: entry('heroFanApplicant', 'Hero Fan Bewerber', 'header', 'cards', 'icon-[material-symbols--view-carousel-outline]'),
  heroFanCompany: entry('heroFanCompany', 'Hero Unternehmen – Karten', 'header', 'cards', 'icon-[material-symbols--view-carousel-outline]', { heroSlot: 'company', archived: true }),
  companyPlatformHero: entry('companyPlatformHero', 'Hero Unternehmen – Handschlag (Archiv)', 'header', 'hero', 'icon-[material-symbols--handshake-outline]', { heroSlot: 'company', archived: true }),
  companyPeopleHero: entry('companyPeopleHero', 'Hero Unternehmen – Menschen', 'header', 'hero', 'icon-[material-symbols--groups-outline]', { heroSlot: 'company' }),
  globusCampaignApplicant: entry('globusCampaignApplicant', 'GLOBUS Kampagne Bewerber', 'header', 'hero', 'icon-[material-symbols--campaign-outline]'),
  globusCampaignCompany: entry('globusCampaignCompany', 'GLOBUS Kampagne Unternehmen', 'header', 'hero', 'icon-[material-symbols--campaign-outline]', { heroSlot: 'company' }),
  companyObjections: entry('companyObjections', 'Unternehmen – Fragen & Antworten', 'content', 'cards', ''),
  companyMatching: entry('companyMatching', 'Unternehmen – Matching erleben', 'conversion', 'split', ''),
  intro: entry('intro', 'Willkommen', 'content', 'split', 'icon-[material-symbols--waving-hand-outline]'),
  industryOverview: entry('industryOverview', 'Branchen', 'cards', 'cards', 'icon-[material-symbols--grid-view]'),
  laborShortage: entry('laborShortage', 'Fachkräftemangel', 'content', 'split', 'icon-[material-symbols--group-search]'),
  reasons: entry('reasons', 'Gründe', 'content', 'cards', 'icon-[material-symbols--fact-check-outline]'),
  benefits: entry('benefits', 'Vorteile', 'cards', 'cards', 'icon-[material-symbols--verified-outline]'),
  costs: entry('costs', 'Kosten', 'cards', 'cards', 'icon-[material-symbols--payments-outline]'),
  recognition: entry('recognition', 'Anerkennung', 'content', 'split', 'icon-[material-symbols--workspace-premium-outline]'),
  requirements: entry('requirements', 'Voraussetzungen', 'content', 'cards', 'icon-[material-symbols--checklist]'),
  faqApplicant: entry('faqApplicant', 'FAQ Pflegekräfte', 'conversion', 'form', 'icon-[material-symbols--quiz-outline]'),
  faqCompany: entry('faqCompany', 'FAQ Arbeitgeber', 'conversion', 'form', 'icon-[material-symbols--quiz-outline]'),
  quality: entry('quality', 'Qualität & Integration', 'trust', 'cards', 'icon-[material-symbols--shield-with-heart-outline]'),
  knowledge: entry('knowledge', 'Wissen & Ratgeber', 'cards', 'cards', 'icon-[material-symbols--menu-book-outline]'),
  values: entry('values', 'Werte', 'trust', 'cards', 'icon-[material-symbols--favorite-outline]'),
  consultationApplicant: entry('consultationApplicant', 'Erstberatung', 'conversion', 'form', 'icon-[material-symbols--support-agent]'),
  consultationCompany: entry('consultationCompany', 'Personalbedarf', 'conversion', 'form', 'icon-[material-symbols--business-center]'),
  process: entry('process', 'Ablauf', 'conversion', 'process', 'icon-[material-symbols--route-outline]'),
  team: entry('team', 'Team', 'trust', 'cards', 'icon-[material-symbols--groups-outline]'),
  story: entry('story', 'Story', 'trust', 'quote', 'icon-[material-symbols--format-quote-outline]'),
  partners: entry('partners', 'Partner', 'trust', 'logos', 'icon-[material-symbols--handshake-outline]'),
  globusPartner: entry('globusPartner', 'Globus Partner', 'trust', 'quote', 'icon-[material-symbols--storefront-outline]'),
  info: entry('info', 'Informationen', 'content', 'split', 'icon-[material-symbols--info-outline]'),
  contact: entry('contact', 'Kontakt', 'conversion', 'form', 'icon-[material-symbols--alternate-email]'),
};

const categoryLabels: Record<LandingEditorSectionCategory, string> = {
  header: 'Header & Kampagnen',
  content: 'Inhalte',
  cards: 'Karten & Wissen',
  trust: 'Vertrauen',
  conversion: 'Interaktion',
};

const categoryOrder: readonly LandingEditorSectionCategory[] = ['header', 'content', 'cards', 'trust', 'conversion'];

export const landingEditorSectionLibraryKeys = landingHomeSectionKeys.filter(
  section => !disabledLandingSections.has(section),
);

export const landingEditorSectionLibraryGroups = categoryOrder.map(category => ({
  category,
  label: categoryLabels[category],
  entries: landingEditorSectionLibraryKeys
    .map(section => landingEditorSectionLibrary[section])
    .filter(section => section.category === category),
}));
